import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Autocomplete } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { AsolviDateTimePicker } from "components/AsolviDateTimePicker";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import Transition from "components/Transition";
import { PropertyValueInputType, PropertyValueType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { setOpen } from "store/slices/dialog.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

type AddMachinePropertyDialogProps = {
  loading: boolean;
  updateMachineProperties: (
    values: PropertyValueInputType[],
  ) => Promise<{ success: boolean; queued: boolean; abort: boolean }>;
};

export const AddMachinePropertyDialog: FC<AddMachinePropertyDialogProps> = (props) => {
  const { loading, updateMachineProperties } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectPageSelectedJob);
  const { machineProperties } = useAppSelector((s) => s.cache);
  const open = useAppSelector((s) => s.dialog.addMachineProperty);
  const [selectedProperty, setSelectedProperty] = useState<PropertyValueType | null>(null);
  const [value, setValue] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    dispatch(setOpen({ dialogName: "addMachineProperty", open: false }));
  }, [dispatch]);

  const updateMachineProps = useCallback(async () => {
    if (!value) return;
    const newProperty: PropertyValueInputType = {
      code: selectedProperty?.code!,
      propertyGuid: null,
      sourceGuid: job.machine!.id!,
      value,
    };
    const ret = await updateMachineProperties([newProperty]);
    if (ret.success) {
      handleClose();
      if (ret.queued) {
        dispatch(addSnackbarMessage({ key: "UpdateMachineCustomProps-stored" }));
      } else {
        dispatch(addSnackbarMessage({ key: "UpdateMachineCustomProps-success" }));
      }
    } else if (!ret.success && !ret.abort) {
      dispatch(addSnackbarMessage({ key: "UpdateMachineCustomProps-fail" }));
    }
  }, [dispatch, handleClose, job.machine, selectedProperty?.code, updateMachineProperties, value]);

  const selectedPropertyInput = useCallback(() => {
    if (!selectedProperty) return <></>;

    if (selectedProperty.useValues) {
      return (
        <Autocomplete
          fullWidth
          value={value}
          options={selectedProperty.values}
          onChange={(e, value) => {
            setValue(value);
          }}
          size="small"
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={intl.formatMessage({ id: "machine.selectValue" })}
            />
          )}
          noOptionsText={intl.formatMessage({
            id: "general.noOptions",
          })}
        />
      );
    } else {
      switch (selectedProperty.dataType) {
        case "D":
          return (
            <AsolviDateTimePicker
              value={value ? new Date(value) : null}
              onChange={(date) => {
                if (date) setValue(date.toISOString());
                else setValue("");
              }}
            />
          );
        case "N":
          return (
            <StyledTextField
              fullWidth
              value={value}
              id="description"
              label={intl.formatMessage({ id: "machine.propertyValue" })}
              onChange={(e: any) => {
                const { value } = e.target;
                setValue(value);
              }}
              type="number"
              inputProps={{
                min: selectedProperty.minLen,
                max: selectedProperty.maxLen,
                step: "any",
              }}
            />
          );
        default:
          return (
            <StyledTextField
              fullWidth
              value={value}
              id="description"
              label={intl.formatMessage({ id: "machine.propertyValue" })}
              onChange={(e: any) => {
                const { value } = e.target;
                setValue(value);
              }}
            />
          );
      }
    }
  }, [intl, selectedProperty, value]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="AddMachinePropertyDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="AddMachinePropertyDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            <FormattedMessage id="machine.addProperty" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Card>
        <CardContent>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <Autocomplete
                fullWidth
                value={selectedProperty}
                options={machineProperties}
                getOptionLabel={(o) => o.description}
                onChange={(e, value) => {
                  setSelectedProperty(value);
                  setValue("");
                }}
                size="small"
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "machine.property" })}
                  />
                )}
                noOptionsText={intl.formatMessage({
                  id: "general.noOptions",
                })}
              />
            </Grid>
            {selectedProperty && <Grid item>{selectedPropertyInput()}</Grid>}

            <Grid item>
              <PrimaryButton
                key={`${loading}`}
                disabled={loading || !value}
                fullWidth
                onClick={() => updateMachineProps()}
                data-testid="AddMachinePropertyDialog-SaveButton"
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Dialog>
  );
};
